import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '../utils/api';
import LogoLoader from '@/components/LogoLoader';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const transitionTimeoutRef = useRef(null);
  const [transitioning, setTransitioning] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { user: userData, access_token: accessToken, health } = response.data;

      login(userData, accessToken);

      if (health) {
        const failingChecks = (health.checks ?? [])
          .filter((check) => check.status !== 'ok')
          .map((check) =>
            check.detail ? `${check.name}: ${check.detail}` : check.name,
          )
          .join(' • ');

        const descriptionSegments = [
          `System health: ${health.status.toUpperCase()}`,
          failingChecks || undefined,
        ].filter(Boolean);

        const toastOptions = descriptionSegments.length
          ? { description: descriptionSegments.join(' — ') }
          : undefined;

        if (health.status === 'ok') {
          toast.success('Logged in successfully!', toastOptions);
        } else if (health.status === 'warning') {
          toast.warning('Logged in with system warnings', toastOptions);
        } else {
          toast.error('System health check failed', toastOptions);
        }
      } else {
        toast.success('Logged in successfully!');
      }
      setTransitioning(true);
      transitionTimeoutRef.current = setTimeout(() => {
        navigate('/dashboard', { state: { fromLogin: true } });
      }, 900);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-utis-sage/20 via-white to-utis-sage/20 flex flex-col p-6 overflow-hidden">
      <div className="flex-1 flex items-center justify-center">
        <Card className="w-full max-w-md shadow-xl border-utis-sage/30" data-testid="login-card">
          <CardHeader className="text-center">
            <div className="mb-4 flex justify-center">
              <img
                src="/assets/utis-logo.png"
                alt="UTIS Logo"
                className={`h-20 w-auto ${transitioning ? 'opacity-0' : ''}`}
              />
            </div>
            <CardTitle className="text-3xl font-bold text-utis-forest">UTIS Finance Suite</CardTitle>
            <CardDescription className="text-base mt-2">
              Manage your invoices, clients, payroll, and financial insights — all in one place.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5"
                  data-testid="password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white h-11"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <footer className="pt-6 text-center text-sm text-gray-600">
        © 2025 UTIS LLC | Cloud-powered Financial Platform
      </footer>
      {loading && !transitioning && (
        <LogoLoader message="Signing you in..." fullscreen />
      )}
      {transitioning && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
          <img
            src="/assets/utis-logo.png"
            alt="UTIS Logo"
            className="h-28 w-auto animate-logo-transition"
          />
        </div>
      )}
    </div>
  );
};

export default Login;
